import React, { useEffect, useState } from "react";
import { supabase, fmtBRL, formatError } from "@/lib/supabase";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Link as LinkIcon, Mail, Send, MessageCircle, Sparkles, Wand2 } from "lucide-react";

const STATUS = {
  pendente: { label: "Pendente", cls: "bg-yellow-100 text-yellow-800" },
  enviada: { label: "Enviada", cls: "bg-blue-100 text-blue-800" },
  aceita: { label: "Aceita", cls: "bg-green-100 text-green-800" },
  assinada: { label: "Assinada", cls: "bg-emerald-100 text-emerald-800 border border-emerald-200" },
  recusada: { label: "Recusada", cls: "bg-red-100 text-red-800" },
};

export default function Proposals() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [emailDialog, setEmailDialog] = useState(null); // id da proposta
  const [emailInput, setEmailInput] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ cliente: "", telefone: "", servico: "", valor: "", prazo: "", status: "pendente", mensagem_cliente: "", detalhamento: "" });

  const reload = async () => {
    try {
      const data = await db.select("propostas", { order: "created_at" });
      setItems(data || []);
    } catch (err) { toast.error(formatError(err)); }
  };
  useEffect(() => { if (user) reload(); }, [user]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ cliente: "", telefone: "", servico: "", valor: "", prazo: "", status: "pendente", mensagem_cliente: "", detalhamento: "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({ 
      cliente: p.cliente, 
      telefone: p.telefone, 
      servico: p.servico, 
      valor: p.valor, 
      prazo: p.prazo, 
      status: p.status, 
      mensagem_cliente: p.mensagem_cliente || "", 
      detalhamento: p.detalhamento || "" 
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editingId) {
        await db.update("propostas", editingId, form);
        toast.success("Proposta atualizada");
      } else {
        await db.insert("propostas", { ...form, user_id: user.id });
        toast.success("Proposta criada");
      }
      setOpen(false);
      reload();
    } catch (err) { toast.error(formatError(err)); }
  };

  const upd = async (id, s) => { try { await db.update("propostas", id, { status: s }); reload(); } catch (err) { toast.error(formatError(err)); } };
  const rm = async (id) => {
    if (!window.confirm("Excluir proposta?")) return;
    try { await db.remove("propostas", id); reload(); } catch (err) { toast.error(formatError(err)); }
  };
  const copyLink = (id) => {
    const url = `${window.location.origin}/p/${id}`;
    navigator.clipboard.writeText(url);
    toast.success("Link público copiado!");
  };

  const sendEmail = async () => {
    if (!emailInput.includes("@")) return toast.error("E-mail inválido");
    setSendingEmail(true);
    try {
      await db.sendProposalEmail(emailDialog, emailInput);
      toast.success("E-mail enviado com sucesso!");
      setEmailDialog(null);
      setEmailInput("");
      reload(); // status might have changed to 'enviada'
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSendingEmail(false);
    }
  };

  const sendWhatsApp = async (p) => {
    if (!p.telefone || p.telefone.trim() === "") {
      toast.error("Adicione o telefone do cliente editando a proposta primeiro.");
      return;
    }
    const url = `${window.location.origin}/p/${p.id}`;
    const text = encodeURIComponent(`Olá ${p.cliente}, elaborei a proposta sob medida para o seu projeto de ${p.servico}. Acesse o link oficial para visualizar: ${url}`);
    
    // Remove tudo que não for número do telefone
    const phone = p.telefone.replace(/\D/g, "");
    
    window.open(`https://wa.me/${phone}?text=${text}`, "_blank");
    
    // Opcional: Atualizar status para enviada
    if (p.status === "pendente" || p.status === "rascunho") {
      upd(p.id, "enviada");
    }
  };



  return (
    <div data-testid="proposals-page">
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Propostas</div>
          <h1 className="font-serif text-4xl md:text-5xl">Propostas</h1>
          <p className="text-[#4A4A4A] mt-2">Gerencie suas propostas comerciais.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white" data-testid="new-proposal-btn">
              <Plus className="w-4 h-4 mr-2" /> Nova proposta
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none max-w-2xl">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{editingId ? "Editar proposta" : "Nova proposta"}</DialogTitle></DialogHeader>
            
            <Tabs defaultValue="basic" className="w-full mt-2">
              <TabsList className="grid w-full grid-cols-2 rounded-none mb-4">
                <TabsTrigger value="basic" className="rounded-none">Dados Básicos</TabsTrigger>
                <TabsTrigger value="spin" className="rounded-none">Descrição do Serviço</TabsTrigger>
              </TabsList>
              
              <TabsContent value="basic" className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-mono uppercase">Cliente</Label>
                    <Input value={form.cliente} onChange={(e) => setForm({ ...form, cliente: e.target.value })} className="rounded-none" data-testid="prop-client" /></div>
                  <div><Label className="text-xs font-mono uppercase">Telefone (WhatsApp)</Label>
                    <Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="rounded-none" placeholder="11999999999" /></div>
                </div>
                <div><Label className="text-xs font-mono uppercase">Serviço</Label>
                  <Input value={form.servico} onChange={(e) => setForm({ ...form, servico: e.target.value })} className="rounded-none" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs font-mono uppercase">Valor</Label>
                    <Input value={form.valor} onChange={(e) => setForm({ ...form, valor: e.target.value })} className="rounded-none" placeholder="R$ 500" /></div>
                  <div><Label className="text-xs font-mono uppercase">Prazo</Label>
                    <Input value={form.prazo} onChange={(e) => setForm({ ...form, prazo: e.target.value })} className="rounded-none" placeholder="10 dias" /></div>
                </div>
                <div><Label className="text-xs font-mono uppercase">Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select></div>
              </TabsContent>
              
              <TabsContent value="spin" className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs font-mono uppercase">Descrição do Serviço</Label>
                  </div>
                  <Textarea 
                    value={form.detalhamento} 
                    onChange={(e) => setForm({ ...form, detalhamento: e.target.value })} 
                    className="rounded-none h-64 font-serif leading-relaxed"
                    placeholder="Escreva aqui a descrição detalhada do que será feito no serviço..."
                  />
                </div>
              </TabsContent>
            </Tabs>
            
            <div className="mt-4">
              <Button onClick={save} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white w-full" data-testid="prop-save-btn">
                {editingId ? "Salvar Alterações" : "Salvar Proposta"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Modal de E-mail */}
        <Dialog open={!!emailDialog} onOpenChange={(v) => { if(!v) setEmailDialog(null) }}>
          <DialogContent className="rounded-none sm:max-w-[425px]">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Enviar por E-mail</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-2">
              <p className="text-sm text-[#4A4A4A]">A proposta será enviada com um link para visualização pública e o seu nome.</p>
              <div>
                <Label className="text-xs font-mono uppercase">E-mail do Cliente</Label>
                <Input 
                  value={emailInput} 
                  onChange={(e) => setEmailInput(e.target.value)} 
                  className="rounded-none mt-1" 
                  placeholder="cliente@email.com" 
                  type="email"
                />
              </div>
              <Button 
                onClick={sendEmail} 
                disabled={sendingEmail}
                className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white w-full"
              >
                {sendingEmail ? "Enviando..." : <><Send className="w-4 h-4 mr-2" /> Enviar Agora</>}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {items.length === 0 ? (
        <Card className="p-16 text-center border border-black/10 rounded-none bg-white">
          <p className="text-[#8A8A8A] font-mono">Nenhuma proposta ainda.</p>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((p) => (
            <Card key={p.id} className="p-6 bg-white border border-black/10 rounded-none" data-testid={`proposal-${p.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-serif text-2xl">{p.cliente}</h3>
                    <Badge className={`${STATUS[p.status]?.cls || ""} rounded-none`}>{STATUS[p.status]?.label || p.status}</Badge>
                  </div>
                  <div className="text-sm text-[#4A4A4A] font-mono">{p.servico} · {p.prazo} · {p.valor ? fmtBRL(p.valor) : "A combinar"}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => openEdit(p)} variant="outline" className="rounded-none">
                    Editar
                  </Button>
                  <Button onClick={() => copyLink(p.id)} variant="outline" className="rounded-none" data-testid={`copy-link-${p.id}`}>
                    <LinkIcon className="w-4 h-4 mr-2" /> Link
                  </Button>
                  <Button onClick={() => setEmailDialog(p.id)} variant="outline" className="rounded-none">
                    <Mail className="w-4 h-4 mr-2" /> E-mail
                  </Button>
                  <Button onClick={() => sendWhatsApp(p)} variant="outline" className="rounded-none text-green-700 hover:text-green-800 hover:bg-green-50 border-green-200">
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                  <Select value={p.status} onValueChange={(v) => upd(p.id, v)}>
                    <SelectTrigger className="rounded-none w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" className="rounded-none" onClick={() => rm(p.id)}>
                    <Trash2 className="w-4 h-4 text-[#D9381E]" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
